import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { SettingsNav } from '../../components/SettingsNav';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { relativeTime, shortDate } from '../../lib/format';
import { describeDevice, passkeysSupported, registerPasskey } from '../../lib/passkeys';

export function PasskeysPage() {
  const qc = useQueryClient();
  const supported = passkeysSupported();
  const list = useQuery({ queryKey: queryKeys.passkeys, queryFn: () => api.auth.passkeys.list() });
  const invalidate = () => qc.invalidateQueries({ queryKey: queryKeys.passkeys });
  const add = useMutation({
    mutationFn: () => registerPasskey(describeDevice()),
    onSuccess: invalidate,
  });
  const rename = useMutation({
    mutationFn: (v: { id: string; deviceName: string }) =>
      api.auth.passkeys.rename(v.id, { deviceName: v.deviceName }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.auth.passkeys.remove(id),
    onSuccess: invalidate,
  });
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <AppShell title="Passkeys">
      <SettingsNav />
      <p className="mb-3 text-sm text-muted">
        Passkeys let you sign in with your face, fingerprint or device PIN. Your password keeps
        working as a fallback.
      </p>
      {!supported ? <Alert tone="warning">This browser doesn’t support passkeys.</Alert> : null}
      {list.isError ? <Alert tone="error">{messageForError(list.error)}</Alert> : null}
      {add.isError ? <Alert tone="error">{messageForError(add.error)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
      <ul className="mb-4 flex flex-col gap-2">
        {list.data?.passkeys.map((p) => (
          <li key={p.id} className="card p-4">
            {editing === p.id ? (
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const deviceName = String(
                    new FormData(e.currentTarget).get('deviceName') ?? '',
                  ).trim();
                  if (deviceName)
                    rename.mutate({ id: p.id, deviceName }, { onSuccess: () => setEditing(null) });
                }}
              >
                <label className="sr-only" htmlFor={`pk-${p.id}`}>
                  Name
                </label>
                <input
                  id={`pk-${p.id}`}
                  name="deviceName"
                  defaultValue={p.deviceName ?? ''}
                  className="min-h-10 flex-1 rounded-lg border border-line px-3"
                  autoFocus
                />
                <button
                  type="submit"
                  className="min-h-10 rounded-lg bg-brand px-3 text-sm font-semibold text-white"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="min-h-10 rounded-lg border border-line px-3 text-sm"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{p.deviceName ?? 'Passkey'}</p>
                  <p className="text-xs text-muted">
                    Added {shortDate(p.createdAt)} ·{' '}
                    {p.lastUsedAt ? `used ${relativeTime(p.lastUsedAt)}` : 'never used'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(p.id)}
                    className="min-h-9 rounded-lg border border-line px-3 text-sm"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => remove.mutate(p.id)}
                    disabled={remove.isPending}
                    className="min-h-9 rounded-lg border border-line px-3 text-sm text-fail hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
        {list.data && list.data.passkeys.length === 0 ? (
          <li className="text-sm text-muted">No passkeys yet.</li>
        ) : null}
      </ul>
      <Button onClick={() => add.mutate()} disabled={!supported || add.isPending}>
        {add.isPending ? 'Follow the prompt…' : 'Add a passkey for this device'}
      </Button>
    </AppShell>
  );
}
